import type { OfferFieldChange, OfferRejectionReasonCode } from './offers';

/**
 * Spec 081b — partner packages, admin side (design §3 "Admin — /api/garages/admin/packages").
 * Field names mirror the garages DTOs `AdminPackageRow`, `PackageResponse`, `PackageRequest` and
 * `AdminPackageDetail`. Money arrives as JSON numbers, `startsAt`/`endsAt` as `YYYY-MM-DD`
 * (Asia/Dubai LocalDate), timestamps as ISO instants.
 */

export type PackageStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'SCHEDULED'
  | 'LIVE'
  | 'PAUSED'
  | 'ENDED'
  | 'REJECTED'
  | 'SUSPENDED';

/** Derived server-side (D2): FIXED when every choice is fixed and no item is car-specific. */
export type PackagePricingMode = 'FIXED' | 'PERCENT';

/** Statuses the admin can suspend (SCHEDULED/LIVE/PAUSED → SUSPENDED). */
export const PACKAGE_SUSPENDABLE_STATUSES: readonly PackageStatus[] = ['SCHEDULED', 'LIVE', 'PAUSED'];

/** The queue's status filter: the review queue plus the running statuses (suspend access). */
export const PACKAGE_QUEUE_STATUSES: readonly PackageStatus[] = ['PENDING_APPROVAL', 'SCHEDULED', 'LIVE', 'PAUSED'];

/** Reject codes: the admin DecisionBar is shared with offers, so packages use the offer reason codes and labels. */
export type PackageRejectionReasonCode = OfferRejectionReasonCode;

export interface PackageCover {
  storageKey: string;
  downloadUrl: string | null;
}

export interface PackageFixedChoice {
  variationId: string;
  variationName: string;
  variationNameAr: string | null;
  valueId: string;
  label: string;
  labelAr: string | null;
}

export interface PackageOpenVariation {
  variationId: string;
  name: string;
  nameAr: string | null;
  selectionMode: 'SINGLE' | 'MULTI';
}

export interface PackageItemView {
  offeringId: string;
  name: string;
  nameAr: string | null;
  headline: string | null;
  headlineAr: string | null;
  isCarSpecific: boolean;
  eligible: boolean;
  ineligibleReason: string | null;
  fixedChoices: PackageFixedChoice[];
  openVariations: PackageOpenVariation[];
  /** FIXED: the fixed-combo list price; PERCENT: the "from" price. */
  listPrice: number | null;
  durationMinutes: number | null;
}

export interface PackageResponse {
  id: string;
  garageId: string;
  status: PackageStatus;
  pricingMode: PackagePricingMode;
  nameEn: string;
  nameAr: string | null;
  descriptionEn: string | null;
  descriptionAr: string | null;
  cover: PackageCover | null;
  items: PackageItemView[];
  originalTotal: number | null;
  fromTotal: number | null;
  packagePrice: number | null;
  discountPercent: number | null;
  savingAmount: number | null;
  savingPercent: number | null;
  fromPrice: number | null;
  durationMinutes: number | null;
  startsAt: string | null;
  endsAt: string | null;
  displayOrder: number | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReasonCode: PackageRejectionReasonCode | null;
  rejectionReason: string | null;
  suspendReason: string | null;
  endReason: 'EXPIRED' | 'PARTNER' | 'ADMIN' | null;
  unavailableSince: string | null;
  unavailableReason: string | null;
  lastEditedAt: string | null;
  lastEditedBy: 'OWNER' | 'ADMIN' | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** One row of `GET /api/garages/admin/packages` and `GET /api/garages/admin/garages/{garageId}/packages`. */
export interface AdminPackageRow {
  id: string;
  garageId: string;
  garageName: string;
  nameEn: string;
  status: PackageStatus;
  pricingMode: PackagePricingMode;
  packagePrice: number | null;
  discountPercent: number | null;
  originalTotal: number | null;
  savingPercent: number | null;
  startsAt: string | null;
  endsAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReasonCode: PackageRejectionReasonCode | null;
  unavailableReason: string | null;
  itemCount: number;
  updatedAt: string;
}

export interface AdminPackageGarage {
  id: string;
  name: string;
}

/** Optional summary the detail may carry; the history card reads the full list on its own. */
export interface AdminPackageHistorySummary {
  priorRejections?: number;
  lastActionAt?: string | null;
}

/**
 * `GET /api/garages/admin/packages/{id}` — the PackageResponse fields plus the garage and a history summary.
 * `toAdminPackageDetail` also accepts the package nested under `pkg` / `packageResponse` / `package`.
 */
export interface AdminPackageDetail extends PackageResponse {
  garage: AdminPackageGarage;
  history?: AdminPackageHistorySummary | null;
  /** Server hint (offers' detector), when sent; the checklist also scans the text itself. */
  contactDetailsInText?: boolean;
  /** True when Arabic is mandatory for this package (D-090 Q2: optional by default). */
  arabicRequired?: boolean;
}

type RawDetail = Partial<AdminPackageDetail> & {
  pkg?: PackageResponse;
  packageResponse?: PackageResponse;
  package?: PackageResponse;
  garageName?: string;
};

export function toAdminPackageDetail(raw: RawDetail): AdminPackageDetail {
  const nested = raw.pkg ?? raw.packageResponse ?? raw.package;
  const base = (nested ?? raw) as PackageResponse;
  const garage = raw.garage ?? { id: base.garageId, name: raw.garageName ?? '' };
  return {
    ...base,
    items: base.items ?? [],
    garage,
    history: raw.history ?? null,
    contactDetailsInText: raw.contactDetailsInText,
    arabicRequired: raw.arabicRequired,
  };
}

export interface PackageItemRequest {
  offeringId: string;
  fixedVariationValueIds: string[];
}

/** `PUT /api/garages/admin/packages/{id}` — the partner PackageRequest with `version` required. */
export interface PackageRequest {
  nameEn: string;
  nameAr: string | null;
  descriptionEn: string | null;
  descriptionAr: string | null;
  coverStorageKey: string | null;
  items: PackageItemRequest[];
  packagePrice: number | null;
  discountPercent: number | null;
  startsAt: string | null;
  endsAt: string | null;
  version: number;
}

export interface ApprovePackageRequest {
  version: number;
}

export interface RejectPackageRequest {
  reasonCode: PackageRejectionReasonCode;
  reason: string;
  version: number;
}

export interface SuspendPackageRequest {
  reason: string;
  version: number;
}

export interface PackageListParams {
  status?: PackageStatus;
  garageId?: string;
  page?: number;
  size?: number;
}

export type PackageHistoryAction =
  | 'PACKAGE_APPROVED'
  | 'PACKAGE_REJECTED'
  | 'PACKAGE_SUSPENDED'
  | 'PACKAGE_ENDED'
  | 'PACKAGE_ADMIN_EDITED';

/** One row of `GET /api/garages/admin/packages/{id}/history` (garages `AdminActionView`, newest first). */
export interface PackageHistoryEntry {
  id: number;
  occurredAt: string;
  /** `system` for automatic actions (the sweep ending a package). */
  adminSubjectId: string | null;
  adminName: string | null;
  action: PackageHistoryAction | string;
  reason: string | null;
  packageId?: string | null;
  packageName?: string | null;
  changes?: OfferFieldChange[] | null;
}
